import { useCallback, useEffect, useState } from 'react';
import axios from '../utils/axios';
import {
  DEFAULT_LIST_PAGE,
  DEFAULT_LIST_PAGE_SIZE,
  DEFAULT_LOOKUP_PAGE_SIZE,
  PRODUCT_SELECTOR_RESULT_PAGE_SIZE,
} from '../constants';
import { getAllBrands } from './useBrands';
import { getAllMarkets } from './useMarkets';
import type {
  BrandRecord,
  MarketRecord,
  PagedResult,
  ProductCategoryRecord,
  ProductRecord,
  ProductSubspeciesRecord,
  ProductTotalsParams,
} from '../types/index';

type UseProductsParams = ProductTotalsParams & {
  enabled?: boolean;
};

type UseProductsResult = {
  data: ProductRecord[];
  error: unknown;
  isLoading: boolean;
  refetch: () => Promise<void>;
  total: number;
};

type ProductLookupMaps = {
  brandNameById?: Record<string, string>;
  marketNameById?: Record<string, string>;
};

const PRODUCT_CATEGORY_NAMES = ['Flower', 'Edible', 'Vape', 'Topical'];

// Hard ceiling on total product/brandless page requests so a backend that keeps returning full pages cannot spin forever.
const MAX_PRODUCT_LOOKUP_PAGES = 200;

function buildBrandNameById(brands: BrandRecord[] = []) {
  return Object.fromEntries((brands || []).map((brand) => [String(brand.id), brand.name || '']));
}

function buildMarketNameById(markets: MarketRecord[] = []) {
  return Object.fromEntries((markets || []).map((market) => [
    String(market.id),
    market.name || market.code || '',
  ]));
}

function normalizeProductRecord(
  product: ProductRecord,
  {
    brandNameById = {},
    marketNameById = {},
  }: {
    brandNameById?: Record<string, string>;
    marketNameById?: Record<string, string>;
  } = {}
): ProductRecord {
  const rawCategoryIndex = Number(
    product?.productCategoryTypeId ??
    product?.productCategoryId ??
    (product as any)?.productCategoryType
  );
  const categoryName = product?.productCategoryName ||
    product?.categoryName ||
    PRODUCT_CATEGORY_NAMES[rawCategoryIndex] ||
    'Unknown';
  const marketIds = Array.isArray(product?.marketIds) ? product.marketIds : [];
  const markets = marketIds
    .map((id) => marketNameById[String(id)])
    .filter(Boolean);
  const archived = typeof product?.isArchived === 'boolean'
    ? product.isArchived
    : product?.active === false;

  return {
    ...product,
    brand: product?.brand || brandNameById[String(product?.brandId ?? '')] || '',
    brandName: product?.brandName || brandNameById[String(product?.brandId ?? '')] || '',
    markets,
    market: product?.market || '',
    category: product?.category || categoryName,
    categoryName,
    productCategoryName: product?.productCategoryName || categoryName,
    notesInternal: (product)?.notesInternal || (product)?.notes || '',
    imageName: (product)?.imageName || '',
    isArchived: archived,
    archivedAt: archived
      ? ((product)?.archivedAt || (product)?.lastModified
        ? new Date((product)?.archivedAt || (product)?.lastModified).toISOString()
        : new Date().toISOString())
      : null,
  };
}

async function resolveProductLookups(organizationId?: number | string | null) {
  if (!organizationId) {
    return {
      brandNameById: {},
      marketNameById: {},
    };
  }

  const [brands, markets] = await Promise.all([
    getAllBrands(organizationId, DEFAULT_LOOKUP_PAGE_SIZE).catch(() => []),
    getAllMarkets(organizationId).catch(() => []),
  ]);

  return {
    brandNameById: buildBrandNameById(brands),
    marketNameById: buildMarketNameById(markets),
  };
}

function normalizePagedProducts(
  payload: any,
  page: number,
  pageSize: number,
  lookups: {
    brandNameById?: Record<string, string>;
    marketNameById?: Record<string, string>;
  } = {}
): PagedResult<ProductRecord> {
  return {
    page: Number(payload?.page ?? page),
    pageSize: Number(payload?.pageSize ?? pageSize),
    total: Number(payload?.total ?? 0),
    data: Array.isArray(payload?.data)
      ? payload.data.map((product: ProductRecord) => normalizeProductRecord(product, lookups))
      : Array.isArray(payload?.documents)
        ? payload.documents.map((product: ProductRecord) => normalizeProductRecord(product, lookups))
        : Array.isArray(payload)
          ? payload.map((product: ProductRecord) => normalizeProductRecord(product, lookups))
          : [],
  };
}

export async function getProductsTotals({
  organizationId,
  marketId = null,
  brandId = null,
  active = null,
  name = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  nameSortAscending = true,
  lastModifiedSortAscending = null,
  sortField = null,
  sortAscending = null,
}: ProductTotalsParams = {}): Promise<PagedResult<ProductRecord>> {
  const lookups = await resolveProductLookups(organizationId);
  const response = await axios.get('/product/totals', {
    params: {
      organizationId,
      marketId,
      brandId,
      active,
      name,
      page,
      pageSize,
      nameSortAscending,
      lastModifiedSortAscending,
      sortField,
      sortAscending,
    },
  });

  return normalizePagedProducts(response.data, page, pageSize, lookups);
}

export function getProductTotalsAbortable({
  organizationId,
  marketId = null,
  brandId = null,
  active = null,
  name = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  nameSortAscending = true,
  lastModifiedSortAscending = null,
  sortField = null,
  sortAscending = null,
  brandNameById = {},
  marketNameById = {},
}: ProductTotalsParams & ProductLookupMaps = {}) {
  const controller = new AbortController();
  const promise = axios.get('/product/totals', {
    params: {
      organizationId,
      marketId,
      brandId,
      active,
      name,
      page,
      pageSize,
      nameSortAscending,
      lastModifiedSortAscending,
      sortField,
      sortAscending,
    },
    signal: controller.signal,
  }).then((response) => normalizePagedProducts(response.data, page, pageSize, {
    brandNameById,
    marketNameById,
  }));

  return {
    promise,
    cancel: () => controller.abort(),
    signal: controller.signal,
  };
}

export async function getProduct(
  organizationId: number | string,
  id: number | string
): Promise<ProductRecord | null> {
  const response = await axios.get('/product/byId', {
    params: { organizationId, id },
  });
  return response.data || null;
}

export function useProduct(
  organizationId: number | string | null | undefined,
  id: number | string | null | undefined,
) {
  const key = organizationId && id ? `${organizationId}:${id}` : null;
  const [requestVersion, setRequestVersion] = useState(0);
  const [state, setState] = useState<{
    key: string | null;
    data: ProductRecord | null;
    error: unknown;
    isLoading: boolean;
  }>({ key, data: null, error: null, isLoading: Boolean(key) });

  useEffect(() => {
    if (!key || !organizationId || !id) {
      setState({ key: null, data: null, error: null, isLoading: false });
      return;
    }
    let cancelled = false;
    setState({ key, data: null, error: null, isLoading: true });
    getProduct(organizationId, id)
      .then((result) => {
        if (!cancelled) {
          setState({
            key,
            data: result,
            error: result ? null : new Error('Product details were not found.'),
            isLoading: false,
          });
        }
      })
      .catch((error) => {
        if (!cancelled) setState({ key, data: null, error, isLoading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, id, key, requestVersion]);

  const refetch = useCallback(async () => {
    if (key) setRequestVersion((current) => current + 1);
  }, [key]);

  const isCurrent = state.key === key;
  return {
    data: isCurrent ? state.data : null,
    error: isCurrent ? state.error : null,
    isLoading: Boolean(key) && (!isCurrent || state.isLoading),
    refetch,
  };
}

type ProductsListParams = {
  organizationId: number | string;
  name?: string | null;
  brandId?: number | string | null;
  marketId?: number | string | null;
  active?: boolean | null;
  page?: number;
  pageSize?: number;
  includeBrandless?: boolean;
};

type ProductQueryParams = {
  organizationId: number | string;
  name: string | null;
  brandId: number | string | null;
  marketId: number | string | null;
  active: boolean | null;
  page: number;
  pageSize: number;
};

async function fetchProductPage(params: ProductQueryParams): Promise<ProductRecord[]> {
  const response = await axios.get('/product', { params });
  return Array.isArray(response.data) ? response.data : [];
}

// Returns whatever products were retrieved plus whether every lookup ran to completion.
export async function getProductsListWithStatus({
  organizationId,
  name = null,
  brandId = null,
  marketId = null,
  active = true,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE,
  includeBrandless = false,
}: ProductsListParams): Promise<{ products: ProductRecord[]; complete: boolean }> {
  const filters = { organizationId, name, brandId, marketId, active };
  const mergesBrandless = includeBrandless
    && page === DEFAULT_LIST_PAGE
    && !brandId
    && !marketId
    && !name;

  if (!mergesBrandless) {
    return { products: await fetchProductPage({ ...filters, page, pageSize }), complete: true };
  }

  // The backend caps a page at 500 for non-super-users, so a larger request would never signal the last page.
  const lookupPageSize = Math.min(pageSize, DEFAULT_LOOKUP_PAGE_SIZE);
  const products: ProductRecord[] = [];
  let pageBudget = MAX_PRODUCT_LOOKUP_PAGES;
  let complete = false;

  for (let offset = 0; pageBudget > 0; offset += 1) {
    pageBudget -= 1;
    let results: ProductRecord[];
    try {
      results = await fetchProductPage({
        ...filters,
        page: DEFAULT_LIST_PAGE + offset,
        pageSize: lookupPageSize,
      });
    } catch (error) {
      if (offset === 0) throw error;
      console.error('[useProducts] Failed to fetch products page', offset, error);
      break;
    }
    products.push(...results);
    if (results.length < lookupPageSize) {
      complete = true;
      break;
    }
  }

  const brandlessProducts: ProductRecord[] = [];
  let brandlessComplete = false;

  for (let offset = 0; pageBudget > 0; offset += 1) {
    pageBudget -= 1;
    let results: ProductRecord[];
    try {
      results = await getBrandlessProducts(organizationId, DEFAULT_LIST_PAGE + offset, lookupPageSize);
    } catch (error) {
      console.error('[useProducts] Failed to fetch brandless products page', offset, error);
      break;
    }
    brandlessProducts.push(...results);
    if (results.length < lookupPageSize) {
      brandlessComplete = true;
      break;
    }
  }

  const matchingBrandlessProducts = brandlessProducts.filter((product) => {
    if (active == null) return true;
    const isActive = product.active !== false && product.isArchived !== true;
    return active ? isActive : !isActive;
  });
  const seenIds = new Set(products.map((product) => String(product?.id ?? '')));
  return {
    products: [
      ...products,
      ...matchingBrandlessProducts.filter((product) => {
        const id = String(product?.id ?? '');
        if (!id || seenIds.has(id)) return false;
        seenIds.add(id);
        return true;
      }),
    ],
    complete: complete && brandlessComplete,
  };
}

export async function getProductsList(params: ProductsListParams): Promise<ProductRecord[]> {
  const { products } = await getProductsListWithStatus(params);
  return products;
}

export async function getBrandlessProducts(
  organizationId: number | string,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE,
): Promise<ProductRecord[]> {
  const response = await axios.get('/product/brandless', {
    params: { organizationId, page, pageSize },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getProductCategories({
  organizationId,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE,
}: {
  organizationId: number | string;
  page?: number;
  pageSize?: number;
}): Promise<ProductCategoryRecord[]> {
  const response = await axios.get('/product/productcategories', {
    params: { organizationId, page, pageSize },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getProductSubspecies({
  organizationId,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE,
}: {
  organizationId: number | string;
  page?: number;
  pageSize?: number;
}): Promise<ProductSubspeciesRecord[]> {
  const response = await axios.get('/product/subspecies', {
    params: { organizationId, page, pageSize },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function addProduct(payload: unknown): Promise<unknown> {
  const response = await axios.post('/product/add', payload);
  return response.data;
}

export async function editProduct(payload: unknown): Promise<unknown> {
  const response = await axios.post('/product/edit', payload);
  return response.data;
}

export async function removeProduct(productId: number | string): Promise<unknown> {
  const response = await axios.delete('/product/remove', {
    params: { productId },
  });
  return response.data;
}

export async function unarchiveProduct(productId: number | string): Promise<unknown> {
  const response = await axios.post('/product/unarchive', JSON.stringify(productId), {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
}

export default function useProducts({
  organizationId,
  marketId = null,
  brandId = null,
  active = null,
  name = null,
  page = DEFAULT_LIST_PAGE,
  pageSize = PRODUCT_SELECTOR_RESULT_PAGE_SIZE,
  nameSortAscending = true,
  lastModifiedSortAscending = null,
  enabled = true,
}: UseProductsParams = {}): UseProductsResult {
  const [data, setData] = useState<ProductRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<unknown>(null);

  const refetch = async () => {
    if (!enabled || !organizationId) {
      setData([]);
      setTotal(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const results = await getProductsTotals({
        organizationId,
        marketId,
        brandId,
        active,
        name,
        page,
        pageSize,
        nameSortAscending,
        lastModifiedSortAscending,
      });
      setData(results.data);
      setTotal(results.total);
    } catch (nextError) {
      setError(nextError);
      setData([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refetch().catch(() => null);
  }, [
    active,
    brandId,
    enabled,
    lastModifiedSortAscending,
    marketId,
    name,
    nameSortAscending,
    organizationId,
    page,
    pageSize,
  ]);

  return {
    data,
    error,
    isLoading,
    refetch,
    total,
  };
}
